import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './Checkout.css';

function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { emoji, delivery, startCooldown } = location.state || {};
  const [selectedAddress, setSelectedAddress] = useState(0);
  const [customAddress, setCustomAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: ''
  });

  useEffect(() => {
    // Redirect to home if accessed directly without clicking a present
    if (!location.state || !location.state.emoji) {
      navigate('/');
    }
  }, [location.state, navigate]);

  const addresses = [
    {
      name: 'Rushitha Penikalapati',
      street: '802 Lexington Avenue',
      city: 'New York',
      state: 'NY',
      zip: '10065'
    },
    {
      name: 'Rushitha Penikalapati',
      street: '816 Divisadero Street',
      city: 'San Francisco',
      state: 'CA',
      zip: '94117'
    },
    {
      name: 'Custom Address',
      street: '',
      city: '',
      state: '',
      zip: '',
      isCustom: true
    }
  ];

  const handleCustomAddressChange = (field, value) => {
    setCustomAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOrder = () => {
    // Use custom address if selected, otherwise use preset address
    const selectedAddr = addresses[selectedAddress].isCustom ? customAddress : addresses[selectedAddress];

    // Prepare email parameters for order notification (to you)
    const templateParams = {
      to_name: 'Rushi',
      item_emoji: emoji,
      item_name: location.state.id,
      recipient_name: selectedAddr.name,
      street: selectedAddr.street,
      city: selectedAddr.city,
      state: selectedAddr.state,
      zip: selectedAddr.zip,
      full_address: `${selectedAddr.street}, ${selectedAddr.city}, ${selectedAddr.state} ${selectedAddr.zip}`
    };

    // Prepare confirmation email parameters (to Rushi)
    const confirmationParams = {
      to_name: 'Rushi',
      item_emoji: emoji,
      item_name: location.state.id,
      recipient_name: selectedAddr.name,
      street: selectedAddr.street,
      city: selectedAddr.city,
      state: selectedAddr.state,
      zip: selectedAddr.zip,
      delivery_time: delivery || 'Free Standard 5-7 Business Day Shipping'
    };

    // Send order notification email (to you)
    emailjs.send(
      'service_8t2iaem',
      'template_ydagiud',
      templateParams,
      'd5DjXc_O-PJVYiOHB'
    )
    .then((response) => {
      console.log('Order notification sent successfully!', response.status, response.text);

      // Send confirmation email (to Rushi)
      return emailjs.send(
        'service_8t2iaem',
        'template_x4j97cp',
        confirmationParams,
        'd5DjXc_O-PJVYiOHB'
      );
    })
    .then((response) => {
      console.log('Confirmation email sent successfully!', response.status, response.text);

      // Start cooldown before navigating
      if (startCooldown && window.startCooldown) {
        window.startCooldown();
      }

      // Navigate back to tree with authenticated state
      localStorage.setItem('authenticated', 'true');
      navigate('/', { state: { showTree: true } });
    })
    .catch((error) => {
      console.error('Failed to send email:', error);

      // Start cooldown even if email fails
      if (startCooldown && window.startCooldown) {
        window.startCooldown();
      }

      // Still navigate even if email fails
      localStorage.setItem('authenticated', 'true');
      navigate('/', { state: { showTree: true } });
    });
  };

  // Don't render anything while checking/redirecting
  if (!location.state || !location.state.emoji) {
    return null;
  }

  return (
    <div className="checkout">
      <div className="checkout-container">
        <h1>Checkout</h1>

        <div className="checkout-section">
          <h2>Ship To</h2>
          <div className="address-options">
            {addresses.map((address, index) => (
              <div key={index}>
                <div
                  className={`address-option ${selectedAddress === index ? 'selected' : ''}`}
                  onClick={() => setSelectedAddress(index)}
                >
                  <div className="radio-button">
                    {selectedAddress === index && <div className="radio-selected"></div>}
                  </div>
                  <div className="address-details">
                    {!address.isCustom ? (
                      <>
                        <p className="address-name">{address.name}</p>
                        <p>{address.street}</p>
                        <p>{address.city}, {address.state} {address.zip}</p>
                      </>
                    ) : (
                      <p className="address-name">Custom Address</p>
                    )}
                  </div>
                </div>

                {address.isCustom && selectedAddress === index && (
                  <div className="custom-address-form">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={customAddress.name}
                      onChange={(e) => handleCustomAddressChange('name', e.target.value)}
                      className="custom-input"
                    />
                    <input
                      type="text"
                      placeholder="Street Address"
                      value={customAddress.street}
                      onChange={(e) => handleCustomAddressChange('street', e.target.value)}
                      className="custom-input"
                    />
                    <div className="custom-address-row">
                      <input
                        type="text"
                        placeholder="City"
                        value={customAddress.city}
                        onChange={(e) => handleCustomAddressChange('city', e.target.value)}
                        className="custom-input"
                        style={{ flex: 2 }}
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={customAddress.state}
                        onChange={(e) => handleCustomAddressChange('state', e.target.value)}
                        className="custom-input"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="text"
                        placeholder="ZIP"
                        value={customAddress.zip}
                        onChange={(e) => handleCustomAddressChange('zip', e.target.value)}
                        className="custom-input"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="checkout-section">
          <h2>Delivery</h2>
          <div className="shipping-method">
            <p>{delivery || 'Free Standard 5-7 Business Day Shipping'}</p>
          </div>
        </div>

        <div className="checkout-section">
          <h2>Order Summary</h2>
          <div className="order-summary">
            <div className="summary-row">
              <div className="items-label">
                <span>Items:</span>
                <span className="item-emoji-container">
                  {emoji}
                  <sup className="quantity-badge">1</sup>
                </span>
              </div>
              <span>FREE</span>
            </div>
            <div className="summary-row">
              <span>Shipping:</span>
              <span>FREE</span>
            </div>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>FREE</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>FREE</span>
            </div>
          </div>
        </div>

        <button className="order-button" onClick={handleOrder}>
          Place Order
        </button>
      </div>
    </div>
  );
}

export default Checkout;
